import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

import routes from "routes";
import footerRoutes from "footer.routes";

import bgImage from "assets/images/bg-about-us.jpg";

const subjects = [
  { name: "English Language", icon: "translate" },
  { name: "Mathematics", icon: "calculate" },
  { name: "General Science", icon: "science" },
  { name: "Social Studies", icon: "public" },
  { name: "Religious & Moral Education", icon: "menu_book" },
  { name: "Physical Education", icon: "sports_soccer" },
  { name: "Creative Arts", icon: "palette" },
  { name: "Computer Studies", icon: "computer" },
  { name: "Music", icon: "music_note" },
  { name: "Local Language", icon: "record_voice_over" },
];

const teachingApproaches = [
  {
    title: "Student-Centered Learning",
    description: "We focus on the individual needs of each student, recognizing that every child learns differently. Our teachers adapt their methods to ensure all students can succeed.",
    icon: "person",
  },
  {
    title: "Interactive Teaching",
    description: "Learning is active and engaging at Rose of Sharon. Students participate in discussions, group activities, and hands-on projects that make concepts come alive.",
    icon: "groups",
  },
  {
    title: "Practical Application",
    description: "We connect classroom learning to real-world situations, helping students understand the relevance and application of what they learn.",
    icon: "engineering",
  },
  {
    title: "Continuous Assessment",
    description: "Regular assessments help us track progress and identify areas where students need additional support, ensuring no child is left behind.",
    icon: "assessment",
  },
];

function Academics() {
  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "internal",
          route: "/admissions",
          label: "Enroll Now",
          color: "success",
        }}
        transparent
        light
      />
      <MKBox
        minHeight="75vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              Academics
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.8} mt={1} mb={3}>
              Excellence in education through a comprehensive curriculum
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {/* Curriculum Overview */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={6}>
                <MKTypography variant="h3" mb={2}>
                  Curriculum Overview
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  At Rose of Sharon Primary School, we follow a comprehensive curriculum that meets 
                  national educational standards while incorporating our unique Christ-centered approach. 
                  Our curriculum is designed to develop well-rounded students who excel academically 
                  and grow in character.
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  We offer education from Early Childhood Development (ECD) through Grade 7, with 
                  age-appropriate content and teaching methods at each level. Our curriculum integrates 
                  traditional academic subjects with practical life skills and moral education.
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  The curriculum is regularly reviewed and updated to ensure it remains relevant and 
                  prepares students for the challenges of secondary education and beyond.
                </MKTypography>
              </Grid>
              <Grid item xs={12} lg={6}>
                <MKBox
                  component="img"
                  src="/curriculum-overview.jpg"
                  alt="Students learning"
                  borderRadius="lg"
                  shadow="lg"
                  width="100%"
                  sx={{ objectFit: "cover", height: { xs: 300, lg: 400 } }}
                />
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Subjects Offered */}
        <MKBox component="section" py={6} sx={{ backgroundColor: "#f8f9fa" }}>
          <Container>
            <Grid container justifyContent="center" sx={{ textAlign: "center", mb: 4 }}>
              <Grid item xs={12} md={8}>
                <MKTypography variant="h3" mb={1}>
                  Subjects Offered
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  A well-rounded curriculum covering all essential subject areas.
                </MKTypography>
              </Grid>
            </Grid>
            <Grid container spacing={2} justifyContent="center">
              {subjects.map((subject, index) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                  <MKBox
                    p={2}
                    textAlign="center"
                    borderRadius="lg"
                    sx={{
                      backgroundColor: "white",
                      boxShadow: ({ boxShadows: { sm } }) => sm,
                      transition: "all 0.3s",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: ({ boxShadows: { md } }) => md,
                      },
                    }}
                  >
                    <Icon sx={{ color: "#d32f2f", fontSize: "2rem", mb: 1 }}>{subject.icon}</Icon>
                    <MKTypography variant="body2" fontWeight="medium">
                      {subject.name}
                    </MKTypography>
                  </MKBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        {/* Teaching Approach */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container justifyContent="center" sx={{ textAlign: "center", mb: 4 }}>
              <Grid item xs={12} md={8}>
                <MKTypography variant="h3" mb={1}>
                  Our Teaching Approach
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  Effective teaching methods that engage and inspire students.
                </MKTypography>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              {teachingApproaches.map((approach, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <MKBox display="flex" alignItems="flex-start" p={2}>
                    <MKBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="3.5rem"
                      height="3.5rem"
                      borderRadius="lg"
                      sx={{ backgroundColor: "#006747", flexShrink: 0, mr: 2 }}
                    >
                      <Icon sx={{ color: "#d32f2f", fontSize: "1.5rem" }}>{approach.icon}</Icon>
                    </MKBox>
                    <MKBox>
                      <MKTypography variant="h5" mb={0.5}>
                        {approach.title}
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        {approach.description}
                      </MKTypography>
                    </MKBox>
                  </MKBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        {/* Assessment & Progress */}
        <MKBox component="section" py={6} sx={{ backgroundColor: "#f8f9fa" }}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={6} order={{ xs: 2, lg: 1 }}>
                <MKBox
                  component="img"
                  src="/assessment-progress.jpg"
                  alt="Student studying"
                  borderRadius="lg"
                  shadow="lg"
                  width="100%"
                  sx={{ objectFit: "cover", height: { xs: 300, lg: 350 } }}
                />
              </Grid>
              <Grid item xs={12} lg={6} order={{ xs: 1, lg: 2 }}>
                <MKTypography variant="h3" mb={2}>
                  Assessment & Progress Tracking
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  We use a comprehensive assessment system to monitor student progress and ensure 
                  each child is developing according to their potential. Our approach includes:
                </MKTypography>
                <MKBox component="ul" sx={{ pl: 2 }}>
                  <MKTypography component="li" variant="body1" color="text" mb={1}>
                    <strong>Continuous Assessment:</strong> Regular class tests, assignments, and projects
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="text" mb={1}>
                    <strong>Termly Examinations:</strong> Comprehensive exams at the end of each term
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="text" mb={1}>
                    <strong>Progress Reports:</strong> Detailed reports sent to parents each term
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="text" mb={1}>
                    <strong>Parent-Teacher Meetings:</strong> Regular consultations to discuss student progress
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="text">
                    <strong>Remedial Support:</strong> Additional help for students who need extra assistance
                  </MKTypography>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Technology in Learning */}
        <MKBox
          component="section"
          py={6}
          sx={{
            background: "linear-gradient(135deg, #006747 0%, #004d35 100%)",
            borderRadius: "xl",
            mx: { xs: 1, lg: 3 },
            my: 4,
          }}
        >
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={6}>
                <MKTypography variant="h3" color="white" mb={2}>
                  Technology in Learning
                </MKTypography>
                <MKTypography variant="body1" color="white" opacity={0.9} mb={2}>
                  We recognize the importance of technology in modern education. Our students have 
                  access to a well-equipped computer laboratory where they learn essential digital skills.
                </MKTypography>
                <MKTypography variant="body1" color="white" opacity={0.9} mb={2}>
                  Our technology program includes:
                </MKTypography>
                <MKBox component="ul" sx={{ pl: 2, color: "white" }}>
                  <MKTypography component="li" variant="body1" color="white" opacity={0.9} mb={1}>
                    Basic computer operations and typing skills
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="white" opacity={0.9} mb={1}>
                    Introduction to word processing and spreadsheets
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="white" opacity={0.9} mb={1}>
                    Educational software and learning games
                  </MKTypography>
                  <MKTypography component="li" variant="body1" color="white" opacity={0.9}>
                    Internet safety and digital citizenship
                  </MKTypography>
                </MKBox>
              </Grid>
              <Grid item xs={12} lg={6}>
                <MKBox
                  component="img"
                  src="/technology-learning.jpg"
                  alt="Computer lab"
                  borderRadius="lg"
                  width="100%"
                  sx={{ objectFit: "cover", height: { xs: 300, lg: 350 } }}
                />
              </Grid>
            </Grid>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Academics;
